import React, { useMemo } from 'react';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';

import { UserBorrowsCard, UserBorrowsCardSkeleton } from '../ui/UserBorrowsCard';
import { UserBorrowItem } from '../ui';

import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { ConnectWalletBanner } from '@/widgets/Banners';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/shared/ui';
import { BorrowStatusType } from '@/shared/api/borrow';
import { ServerError } from '@/widgets/ServerError';
import { getUserBorrowsOpenedAction } from '@/shared/store/borrows/actions';

export const useUserBorrowsContent = (
    walletAddress: string | undefined,
    items: UserBorrowItem[],
    selectedBorrowStatusList: BorrowStatusType,
    page: number,
    setPage: (page: number) => void,
    totalPages: number,
    step: number,
    marginAccount: string | undefined,
    hasError: boolean,
) => {
    return useMemo(() => {
        if (!walletAddress) {
            return <ConnectWalletBanner />;
        }

        if (!items.length) {
            let title = 'No Margin Account Yet';
            let subtitle = 'Create a new margin account to get started';
            let icon = null;

            if (selectedBorrowStatusList === BorrowStatusType.SUSPENDED) {
                title = 'No Margin Account Available';
                subtitle =
                    'You don’t have any margin account on hold. Set up a margin account to begin borrowing and leveraging your assets.';
                icon = ClockIcon;
            }
            if (selectedBorrowStatusList === BorrowStatusType.CLOSED) {
                title = 'You don’t have any closed margin accounts.';
                subtitle = "Manage your positions and close accounts when you're done borrowing.";
                icon = ClockIcon;
            }

            return <EmptyBanner customSubtitle={subtitle} customTitle={title} icon={icon} />;
        }

        const cards = items
            .map((item, index) => {
                const isOnCurrentPage = index >= page * step && index < page * step + step;

                if (
                    selectedBorrowStatusList === BorrowStatusType.OPENED &&
                    !item.maintenance &&
                    !item.history
                ) {
                    return isOnCurrentPage ? (
                        <UserBorrowsCardSkeleton
                            key={index}
                            address={item.address}
                            symbol={item.symbol}
                            connectedMarginAccount={marginAccount}
                            status={selectedBorrowStatusList}
                        />
                    ) : null;
                }

                return isOnCurrentPage ? (
                    <UserBorrowsCard
                        key={index}
                        connectedMarginAccount={marginAccount}
                        status={selectedBorrowStatusList}
                        {...item}
                    />
                ) : null;
            })
            .filter(Boolean);

        return (
            <>
                {hasError ? (
                    <ServerError action={getUserBorrowsOpenedAction} />
                ) : (
                    <div className='overflow-x-auto'>
                        <div className='grid grid-cols-3 gap-4 mt-6 min-w-[1100px]'>
                            {cards}
                            {page === 0 && items.length < 3}
                        </div>
                        {totalPages > 1 && (
                            <div className='flex justify-end items-center p-5'>
                                <Pagination>
                                    <PaginationContent>
                                        <PaginationPrevious
                                            onClick={() => setPage(page > 0 ? page - 1 : 0)}
                                            disabled={page === 0}
                                            className='p-3 h-[42px] [&_svg_path]:fill-primary'
                                        />

                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <PaginationItem key={index}>
                                                <PaginationLink
                                                    isActive={page === index}
                                                    onClick={() => setPage(index)}
                                                    className={`min-w-[40px] h-[40px] font-bold p-3 text-secondary text-sm leading-none ${
                                                        page === index
                                                            ? 'rounded border border-opaque bg-inversePrimary text-inversePrimary'
                                                            : ''
                                                    }`}
                                                >
                                                    {index + 1}
                                                </PaginationLink>
                                            </PaginationItem>
                                        ))}

                                        <PaginationNext
                                            onClick={() => {
                                                setPage(Math.min(page + 1, totalPages - 1));
                                            }}
                                            disabled={page === totalPages - 1}
                                            className='p-3 h-[42px] [&_svg_path]:fill-primary'
                                        />
                                    </PaginationContent>
                                </Pagination>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    }, [
        walletAddress,
        items,
        selectedBorrowStatusList,
        page,
        totalPages,
        step,
        marginAccount,
        setPage,
    ]);
};
